<template>
  <div>
    <!-- Loading -->
    <LoadingOverlay :active="isLoading" />

    <!-- Container -->
    <div class="redirect-container">
      <!-- Error Alert Modal -->
      <div v-if="errorMessage" class="alert-modal">
        <div class="alert-content">
          <img src="/static/icons/error-icon.svg" class="alert-icon" />
          <p class="alert-message">{{ errorMessage }}</p>
          <sc-button
            variant="primary"
            class="btn-block"
            style="height: 45px;"
            v-on:click="navigateToLogin()"
          >
            กลับไปหน้า Login
          </sc-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import LoadingOverlay from "../../../lib/components/LoadingOverlay.vue";
import ScButton from "@/lib/components/ScButton";

// Dependencies
import { baanRueJaiClient } from "@/dependencies";
import { backendAdminClient } from "@/dependencies";

// Utils
import { toCamelCaseObject } from "@/lib/util";
import { Auth, setCookie, unsetCookie } from "@/lib/cookie";
import {
  AuthMicrosoftActionEnum,
  getAuthMicrosoftErrorMessage
} from "../auth-microsoft";

export default {
  name: "AuthMicrosoftPage",
  components: {
    LoadingOverlay,
    ScButton
  },
  data() {
    return {
      isLoading: false,
      errorMessage: ""
    };
  },
  methods: {
    async fetchAdminProfiles() {
      try {
        // Fetch admin profiles
        const response = await backendAdminClient.admin.getProfiles();

        const data = response && response.data;
        const result = data && data.result;

        if (!result) {
          this.errorMessage = "ไม่พบข้อมูลโปรไฟล์ กรุณาติดต่อผู้ดูแลระบบ";
          return;
        }

        this.saveAdminProfileToStorage(result);
      } catch (error) {
        // Set error message
        this.errorMessage =
          error.message || "เกิดข้อผิดพลาดในการดึงข้อมูลโปรไฟล์";

        // Log the error for debugging
        console.error("fetchAdminProfile error:", error);
      }
    },
    saveAdminProfileToStorage(result) {
      // Convert result to camelCase
      const adminProfile = toCamelCaseObject(result);

      // Destructure adminProfile
      const {
        firstName,
        lastName,
        role,
        adminRole,
        id,
        projects = []
      } = adminProfile;

      // Destructure adminRole
      const { roleId, features = [] } = adminRole || {};

      // Validate required fields
      if (!roleId) {
        this.errorMessage = "ไม่พบสิทธิ์การเข้าถึง กรุณาติดต่อผู้ดูแลระบบ";
        return;
      }
      if (!features || features.length === 0) {
        this.errorMessage = "ไม่พบฟีเจอร์การใช้งาน กรุณาติดต่อผู้ดูแลระบบ";
        return;
      }

      // Save admin profile
      localStorage.setItem("adminFirstName", firstName);
      localStorage.setItem("adminLastName", lastName);
      localStorage.setItem("adminRole", role);
      localStorage.setItem("adminRolePermission", JSON.stringify(adminRole));
      localStorage.setItem("adminRoleId", roleId);
      localStorage.setItem("adminId", id);
      localStorage.setItem(
        "adminProjects",
        projects.map(project => project.id).join()
      );

      // Navigate to target page
      this.navigateToTarget();
    },
    async handleAuthAction() {
      this.isLoading = true;
      this.errorMessage = "";

      try {
        // Destructure passport
        const { action, code, credential } =
          this.decodePassport(this.passport) || {};

        // Validate the action
        switch (action) {
          case AuthMicrosoftActionEnum.Login:
            if (!credential) {
              this.errorMessage =
                "ไม่พบข้อมูลการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง";
              return;
            }

            // Destructure credential
            const { accessToken, refreshToken, sessionId } = credential;

            // Set Microsoft login session
            localStorage.setItem("microsoftSessionId", sessionId);

            // Set admin cookies
            setCookie(Auth.AccessToken, accessToken);
            setCookie(Auth.RefreshToken, refreshToken);

            // Set token to backend admin client
            backendAdminClient.setToken(accessToken);
            baanRueJaiClient.setAuthorizationToken(accessToken);

            // Fetch admin profiles
            await this.fetchAdminProfiles();
            break;

          case AuthMicrosoftActionEnum.Logout:
            // Unsubscribe from FCM
            await this.unsubscribeFcm();

            // Clear localStorage
            localStorage.removeItem("microsoftSessionId");
            localStorage.removeItem("adminName");
            localStorage.removeItem("adminRole");
            localStorage.removeItem("adminRolePermission");
            localStorage.removeItem("adminProjects");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");

            // Clear cookies
            unsetCookie(Auth.AccessToken);
            unsetCookie(Auth.RefreshToken);

            // Navigate to login page
            this.navigateToLogin();
            break;

          case AuthMicrosoftActionEnum.Error:
            this.errorMessage = getAuthMicrosoftErrorMessage(code);
            break;

          default:
            this.errorMessage = "เกิดข้อผิดพลาด กรุณาติดต่อผู้ดูแลระบบ";
            break;
        }
      } catch (error) {
        this.errorMessage =
          error.message || "เกิดข้อผิดพลาด กรุณาติดต่อผู้ดูแลระบบ";
      } finally {
        this.isLoading = false;
      }
    },
    decodePassport(encoded) {
      const jsonString = Buffer.from(encoded, "base64").toString("utf-8");

      return JSON.parse(jsonString);
    },
    async getFcmToken(vapidKey, serviceWorkerRegistration) {
      try {
        const token = await this.$messaging.getToken({
          vapidKey,
          serviceWorkerRegistration
        });

        return token;
      } catch (error) {
        throw error;
      }
    },
    async unsubscribeFcm() {
      await navigator.serviceWorker
        .register(window.location.origin + "/static/firebase-messaging-sw.js")
        .then(async registration => {
          // Get the FCM token
          const token = await this.getFcmToken(
            "BKWV_-CAGdA5BgTpGmGILOtAbK-sCsucRDO2d1KswzMeqS8dui-9Qz-8Mj8WC1az4CtRqaD7BFo_5oVa8Lf6K_I",
            registration
          );

          if (this.$messaging && token) {
            try {
              // Unsubscribe from FCM
              await this.$messaging.deleteToken(token);

              // Clear token
              baanRueJaiClient.setAuthorizationToken();
            } catch (error) {
              console.debug(error);
            }
          }
        })
        .catch(error => {
          const message = `Service Worker Error (${error})`;

          console.error(message);
        });
    },
    navigateToTarget() {
      const targetRoute = this.$route.query.redirect
        ? { path: this.$route.query.redirect }
        : { name: "Dashboard" };

      // Navigate to the target route
      this.$router.push(targetRoute);
    },
    navigateToLogin() {
      this.$router.push({ name: "Login" });
    }
  },
  computed: {
    passport() {
      return this.$route.params.passport;
    }
  },
  async mounted() {
    await this.handleAuthAction();
  }
};
</script>

<style scoped>
.redirect-container {
  width: 100vw !important;
  height: 100vh !important;
  display: flex;
  flex-direction: column;
  background-image: url("/static/pictures/background.webp");
  background-size: cover;
  background-position: center;
  position: relative; /* ต้องมีเพื่อให้ ::before ทำงาน */
}

.redirect-container::before {
  content: "";
  position: absolute; /* หรือ fixed ถ้าต้องการครอบเต็ม viewport */
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 0;
  pointer-events: none;
}

/* Alert Modal */
.alert-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}
.alert-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 360px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  text-align: center;
}
.alert-icon {
  width: 100%;
  margin-bottom: 16px;
}
.alert-message {
  font-size: 16px;
  color: #333;
  margin-bottom: 28px;
  white-space: pre-wrap;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/features/auth/pages/AuthMicrosoftPage.vue